'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as React from 'react'

import { useHasTouchInput } from '../../hooks/useHasTouchInput'
import { cn } from '../../utils/cn'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  tooltipContentClassName,
  tooltipTitleClassName
} from './Tooltip'

type ToggletipContextValue = {
  open: boolean
  contentId: string
  /** `byKeyboard` opens with focus moved into the content, so Tab reaches its links. */
  toggle: (byKeyboard: boolean) => void
  openedByKeyboard: React.RefObject<boolean>
} | null

const ToggletipContext = React.createContext<ToggletipContextValue>(null)

type ToggletipProps = {
  children?: React.ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  /** Tooltip mode only. */
  delayDuration?: number
  /** Tooltip mode only. */
  disableHoverableContent?: boolean
}

/**
 * A tooltip that touch users can open. Without touch input it is the regular hover/focus
 * `Tooltip`; when any pointer is coarse (`(any-pointer: coarse)`) it becomes a popover that tap,
 * click, Enter and Space toggle, and that a tap outside or Escape closes.
 *
 * Use it when the trigger exists only to reveal its content (info icons, badges). Keep `Tooltip`
 * for hints on triggers that already do something on tap (buttons, links, clickable rows).
 */
function Toggletip({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  delayDuration,
  disableHoverableContent
}: ToggletipProps) {
  'use memo'

  const hasTouchInput = useHasTouchInput()
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const [hadTouchInput, setHadTouchInput] = React.useState(hasTouchInput)
  const openedByKeyboard = React.useRef(false)
  const contentId = React.useId()
  const open = openProp ?? uncontrolledOpen

  // Losing touch input swaps in the tooltip: close the popover so it doesn't reopen on its own.
  if (hadTouchInput !== hasTouchInput) {
    setHadTouchInput(hasTouchInput)
    if (!hasTouchInput) setUncontrolledOpen(false)
  }

  const setOpen = (next: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(next)
    onOpenChange?.(next)
  }

  if (!hasTouchInput) {
    return (
      <ToggletipContext.Provider value={null}>
        <Tooltip
          open={openProp}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          delayDuration={delayDuration}
          disableHoverableContent={disableHoverableContent}
        >
          {children}
        </Tooltip>
      </ToggletipContext.Provider>
    )
  }

  return (
    <ToggletipContext.Provider
      value={{
        open,
        contentId,
        openedByKeyboard,
        toggle: byKeyboard => {
          openedByKeyboard.current = byKeyboard
          setOpen(!open)
        }
      }}
    >
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        {children}
      </PopoverPrimitive.Root>
    </ToggletipContext.Provider>
  )
}

function ToggletipTrigger({
  onClick,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof TooltipTrigger>) {
  'use memo'

  const context = React.useContext(ToggletipContext)

  if (!context) return <TooltipTrigger onClick={onClick} onKeyDown={onKeyDown} {...props} />

  // The trigger only reveals content: keep the tap away from enclosing clickable rows and links.
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    event.preventDefault()
    event.stopPropagation()
    // Clicks synthesized from Enter or Space carry no click count.
    context.toggle(event.detail === 0)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event)
    // Native buttons already turn Enter and Space into a click.
    if (event.currentTarget instanceof HTMLButtonElement) return
    if (event.defaultPrevented || (event.key !== 'Enter' && event.key !== ' ')) return
    event.preventDefault()
    event.stopPropagation()
    context.toggle(true)
  }

  return (
    <PopoverPrimitive.Trigger
      data-slot="toggletip-trigger"
      aria-controls={context.contentId}
      // Announces the explanation when the popover opens, like the tooltip does.
      aria-describedby={context.open ? context.contentId : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...props}
    />
  )
}

function ToggletipContent({
  className,
  sideOffset = 8,
  title,
  children,
  onClick,
  onOpenAutoFocus,
  ...props
}: React.ComponentProps<typeof TooltipContent> &
  Pick<React.ComponentProps<typeof PopoverPrimitive.Content>, 'onOpenAutoFocus'>) {
  'use memo'

  const context = React.useContext(ToggletipContext)

  if (!context) {
    return (
      <TooltipContent
        className={className}
        sideOffset={sideOffset}
        title={title}
        onClick={onClick}
        {...props}
      >
        {children}
      </TooltipContent>
    )
  }

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="toggletip-content"
        sideOffset={sideOffset}
        // Opening on tap must not move focus (and scroll the page) into the content.
        onOpenAutoFocus={event => {
          onOpenAutoFocus?.(event)
          if (!context.openedByKeyboard.current) event.preventDefault()
        }}
        // React bubbles portal events through the component tree: keep taps on the content away
        // from enclosing clickable rows and links.
        onClick={event => {
          onClick?.(event)
          event.stopPropagation()
        }}
        className={cn(
          tooltipContentClassName,
          'origin-(--radix-popover-content-transform-origin) outline-hidden',
          className
        )}
        {...props}
        id={context.contentId}
      >
        {title && (
          <p data-slot="tooltip-title" className={tooltipTitleClassName}>
            {title}
          </p>
        )}
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

export { Toggletip, ToggletipContent, ToggletipTrigger }
export type { ToggletipProps }
